import React, { useState, useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";
import { T, MAX_PILLS } from "../../constants/todoTokens";
import TaskPill from "./TaskPill";
import DayDetailModal from "./DayDetailModal";

const WEEK_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WeekView = ({ weekStart, direction, taskMap, onTaskClick, onArchive, onDelete, isMobile, isAdmin, currentUserId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);

  // Derived from taskMap so it stays in sync when tasks are archived/deleted
  const modalTasks = useMemo(() => {
    if (!modalDate) return [];
    return taskMap[modalDate.format("YYYY-MM-DD")] || [];
  }, [taskMap, modalDate]);

  const todayKey = dayjs().format("YYYY-MM-DD");
  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));

  const handleMore = (date) => {
    setModalDate(date);
    setModalOpen(true);
  };

  return (
    <>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={weekStart.format("YYYY-MM-DD")}
          initial={{ opacity: 0, x: direction * 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -30 }}
          transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Day-name header row */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: `1px solid ${T.border}` }}>
            {days.map((date, i) => {
              const key     = date.format("YYYY-MM-DD");
              const isToday = key === todayKey;
              return (
                <Box
                  key={i}
                  sx={{
                    py: 1.5, px: 1,
                    textAlign: "center",
                    bgcolor: isToday ? T.primaryLight : (i === 0 || i === 6) ? "#FAFBFC" : "#fff",
                    borderRight: i < 6 ? `1px solid ${T.border}` : "none",
                  }}
                >
                  <Typography sx={{
                    fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
                    letterSpacing: "0.05em", color: isToday ? T.primary : T.textMuted,
                    mb: "4px",
                  }}>
                    {isMobile ? WEEK_LABELS[i][0] : WEEK_LABELS[i]}
                  </Typography>
                  <Box
                    sx={{
                      width: 28, height: 28, borderRadius: "50%",
                      bgcolor: isToday ? T.primary : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      mx: "auto",
                    }}
                  >
                    <Typography sx={{
                      fontSize: "13px", fontWeight: 800,
                      color: isToday ? "#fff" : T.text,
                      lineHeight: 1,
                    }}>
                      {date.date()}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Task cells */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {days.map((date, i) => {
              const key      = date.format("YYYY-MM-DD");
              const tasks    = taskMap[key] || [];
              const shown    = tasks.slice(0, MAX_PILLS);
              const overflow = tasks.length - MAX_PILLS;
              const isToday  = key === todayKey;

              return (
                <Box
                  key={i}
                  onClick={() => tasks.length > 0 && handleMore(date)}
                  sx={{
                    minHeight: { xs: 100, sm: 140, md: 180 },
                    borderRight: i < 6 ? `1px solid ${T.border}` : "none",
                    borderBottom: `1px solid ${T.border}`,
                    bgcolor: isToday ? "#F0FDFA" : (i === 0 || i === 6) ? "#FAFBFC" : "#fff",
                    p: "6px",
                    cursor: tasks.length > 0 ? "pointer" : "default",
                    transition: "background 0.15s",
                    "&:hover": tasks.length > 0 ? { bgcolor: isToday ? "#CCFBF1" : "#F8FFFE" } : {},
                  }}
                >
                  <Stack spacing="3px">
                    {shown.map((task) => (
                      <TaskPill
                        key={task._id}
                        task={task}
                        onClick={onTaskClick}
                        isMobile={isMobile}
                      />
                    ))}
                  </Stack>
                  {overflow > 0 && (
                    <Typography
                      onClick={(e) => { e.stopPropagation(); handleMore(date); }}
                      sx={{
                        fontSize: "10px", fontWeight: 700, color: T.primary,
                        mt: "4px", px: "4px", cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      +{overflow} more
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </motion.div>
      </AnimatePresence>

      <DayDetailModal
        open={modalOpen}
        date={modalDate}
        tasks={modalTasks}
        onClose={() => setModalOpen(false)}
        onTaskClick={onTaskClick}
        onArchive={onArchive}
        onDelete={onDelete}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
      />
    </>
  );
};

export default React.memo(WeekView);
